import {call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects';
import axios, {AxiosResponse} from 'axios';
import {ActionType, getType} from 'typesafe-actions';
// @ts-ignore — @adobe/redux-saga-promise ships no type declarations
import {implementPromiseAction} from '@adobe/redux-saga-promise';

import * as actions from '../../actions';
import {api} from '../../services/axios';
import Config from '../../../config.json';
import JSONFormData from '../../../utils/JSONFormData';
import {Generic} from '../../../services/endpoints';
import {APIErrorInterface, ReduxState} from '../../types';
import {
    AdapterHealth,
    Agent,
    AiAgentsResponse,
    AssignAgentPhoneError,
    AssignAgentPhoneResponse,
    TenantSession,
    VoiceAiConfig,
} from '../../types/AiAgent';
import {UIConfigInfo} from '../../types/Generic';
import {TimeZone} from '../../types/TimeZone';

const VOICE_AI_CONFIG_SECTION = 'voice_ai_configuration';

class AdapterUnauthorizedError extends Error {
    constructor() {
        super('init-session failed');
    }
}

function* createTenantPayload() {
    // Always send the company email — never an individual's own email. If the company
    // email changes, the new value is what propagates to Apifonica on the next init-session.
    const customerEmail: string | null = yield select(
        (state: ReduxState) => state.generic.globalCustomerInfo?.customer_info?.email || null,
    );
    const {customer_info} = yield select((state: ReduxState) => state.generic.globalCustomerInfo);
    const {timeZonesList} = yield select((state: ReduxState) => state.generic);

    return {
        email: customerEmail,
        name: customer_info.name,
        currency: customer_info.iso_4217,
        country: customer_info.country,
        language: customer_info.i_lang,
        timezone: timeZonesList.find((el: TimeZone) => el.i_time_zone === customer_info.i_time_zone)
            ?.time_zone_name,
    };
}

function* refreshTenantOrAwait(): Generator<any, TenantSession, any> {
    const tenantLoading: boolean = yield select((s: ReduxState) =>
        Boolean(s.aiAgent.tenantLoading),
    );
    if (!tenantLoading) {
        yield put(actions.refreshTenant.request());
    }
    const result = yield take([actions.refreshTenant.success, actions.refreshTenant.failure]);
    if (result.type === getType(actions.refreshTenant.failure)) {
        throw new AdapterUnauthorizedError();
    }
    return result.payload as TenantSession;
}

function* withTenantRetry<T>(
    makeRequest: (accessToken: string) => Promise<AxiosResponse<T>>,
): Generator<any, AxiosResponse<T>, any> {
    let tenant: TenantSession | undefined = yield select((s: ReduxState) => s.aiAgent.tenant);

    if (!tenant?.access_token) {
        tenant = yield* refreshTenantOrAwait();
    }

    try {
        return (yield call(makeRequest, tenant!.access_token)) as AxiosResponse<T>;
    } catch (e: any) {
        if (e?.response?.status !== 401) throw e;
        const fresh = yield* refreshTenantOrAwait();
        return (yield call(makeRequest, fresh.access_token)) as AxiosResponse<T>;
    }
}

function* with401InitRetry<T>(
    makeRequest: () => Promise<AxiosResponse<T>>,
): Generator<any, AxiosResponse<T>, any> {
    try {
        return (yield call(makeRequest)) as AxiosResponse<T>;
    } catch (e: any) {
        if (e?.response?.status !== 401) throw e;
        yield* refreshTenantOrAwait();
        return (yield call(makeRequest)) as AxiosResponse<T>;
    }
}

export function* getAdapterHealthSaga() {
    try {
        const res: AxiosResponse<AdapterHealth> = yield* with401InitRetry<AdapterHealth>(() =>
            axios.get(Config.VOICE_AI_ADAPTER_URL + '/health'),
        );
        yield put(actions.getAdapterHealth.success(res.data));
    } catch (e: any) {
        const apiError: APIErrorInterface = e?.response?.data ?? {
            faultcode: 'adapter_health_failed',
            faultstring: e?.message ?? 'adapter health check failed',
        };
        yield put(actions.getAdapterHealth.failure(apiError));
    }
}

export function* getVoiceAiConfigSaga() {
    try {
        const {session_id, csrf_token} = yield select((s: ReduxState) => s.auth);
        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({section_name: VOICE_AI_CONFIG_SECTION});
        const res: AxiosResponse<{ui_config_list: UIConfigInfo[]}> = yield api.post(
            Generic.GetUIConfigList,
            body,
        );
        const raw = res.data?.ui_config_list?.[0]?.value;
        let parsed: VoiceAiConfig | null = null;
        if (raw) {
            const json = JSON.parse(raw);

            if (Array.isArray(json?.services)) {
                parsed = {services: json.services.filter((s: unknown) => typeof s === 'string')};
            } else if (typeof json?.service_name === 'string') {
                parsed = {services: [json.service_name]};
            } else {
                parsed = {services: []};
            }
        }
        yield put(actions.getVoiceAiConfig.success(parsed));
    } catch (e: any) {
        yield put(actions.getVoiceAiConfig.failure(e?.response?.data));
    }
}

export function* updateVoiceAiConfigSaga(
    action: ActionType<typeof actions.updateVoiceAiConfig.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((s: ReduxState) => s.auth);

        const existing: VoiceAiConfig | null | undefined = yield select(
            (s: ReduxState) => s.aiAgent.voiceAiConfig,
        );
        const currentServices = existing?.services ?? [];
        const nextServices = currentServices.includes(action.payload.service_name)
            ? currentServices
            : [...currentServices, action.payload.service_name];

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams({
            ui_config_list: [
                {
                    section_name: VOICE_AI_CONFIG_SECTION,
                    value: JSON.stringify({services: nextServices}),
                },
            ],
        });
        yield api.post(Generic.UpdateUIConfigList, body);
        yield put(actions.updateVoiceAiConfig.success({services: nextServices}));
    } catch (e: any) {
        yield put(actions.updateVoiceAiConfig.failure(e?.response?.data));
    }
}

export function* refreshTenantSaga() {
    try {
        const tenantPayload = yield* createTenantPayload();
        const pbSession: string | undefined = yield select(
            (s: ReduxState) => s.auth.access_token,
        );
        const doInitSession = () =>
            axios.post(
                Config.VOICE_AI_ADAPTER_URL + '/tenants/init-session',
                tenantPayload,
                {
                    headers: {
                        accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${pbSession ?? ''}`,
                    },
                },
            );

        let res: AxiosResponse<TenantSession>;
        try {
            res = yield call(doInitSession);
        } catch (firstErr) {
            res = yield call(doInitSession);
        }
        yield put(actions.refreshTenant.success(res.data));
    } catch (e: any) {
        yield put(
            actions.refreshTenant.failure(
                e?.response?.data ?? {
                    faultcode: 'init_session_failed',
                    faultstring: e?.message ?? 'init-session failed',
                },
            ),
        );
    }
}

export function* getAiAgentsListSaga(
    action: ActionType<typeof actions.getAiAgentsList.request>,
) {
    try {
        const params: Record<string, string | number | undefined> = {
            limit: action.payload?.limit,
            offset: action.payload?.offset,
            name: action.payload?.name,
        };
        const res: AxiosResponse<AiAgentsResponse> = yield* withTenantRetry<AiAgentsResponse>(
            (token) =>
                axios.get<AiAgentsResponse>(Config.VOICE_AI_ADAPTER_URL + '/voice-ai-agents', {
                    params,
                    headers: {
                        accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }),
        );
        yield put(actions.getAiAgentsList.success(res.data));
    } catch (e: any) {
        if (e instanceof AdapterUnauthorizedError) {
            yield put(actions.getAiAgentsList.failure({error: 'unauthorized'}));
            return;
        }
        yield put(
            actions.getAiAgentsList.failure({
                error: e?.response?.data?.details || e?.message || 'unknown',
            }),
        );
    }
}

export function* loadAiAgentsPageSaga(action: any) {
    yield call(implementPromiseAction, action, function* () {
        const {limit, offset, name} = action.payload ?? {};
        const params: Record<string, string | number | undefined> = {limit, offset, name};
        const res: AxiosResponse<AiAgentsResponse> = yield* withTenantRetry<AiAgentsResponse>(
            (token) =>
                axios.get<AiAgentsResponse>(Config.VOICE_AI_ADAPTER_URL + '/voice-ai-agents', {
                    params,
                    headers: {
                        accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }),
        );
        return res.data;
    });
}

export function* assignAgentPhoneSaga(
    action: ActionType<typeof actions.assignAgentPhone.request>,
) {
    const {agent_id} = action.payload;
    try {
        const res: AxiosResponse<AssignAgentPhoneResponse> = yield* withTenantRetry<
            AssignAgentPhoneResponse
        >((token) =>
            axios.patch<AssignAgentPhoneResponse>(
                `${Config.VOICE_AI_ADAPTER_URL}/voice-ai-agents/${encodeURIComponent(agent_id)}`,
                {},
                {
                    headers: {
                        accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                },
            ),
        );
        yield put(actions.assignAgentPhone.success(res.data));
    } catch (e: any) {
        let message = 'unknown';
        if (e instanceof AdapterUnauthorizedError) {
            message = 'unauthorized';
        } else if (e?.response?.status === 422) {

            const detail = e?.response?.data?.detail;
            if (typeof detail === 'string') {
                message = detail;
            } else if (Array.isArray(detail)) {
                message = detail[0]?.msg ?? 'Validation failed';
            } else {
                message = 'Validation failed';
            }
        } else {
            message = e?.response?.data?.message || e?.message || 'unknown';
        }
        const failure: AssignAgentPhoneError = {agent_id, message};
        yield put(actions.assignAgentPhone.failure(failure));
    }
}

export function* findAiAgentBySipUriSaga(
    action: ActionType<typeof actions.findAiAgentBySipUri.request>,
) {
    const {sipUri} = action.payload;
    try {
        const limit = 50;
        let offset = 0;
        let total = Number.POSITIVE_INFINITY;
        let match: Agent | null = null;

        while (!match && offset < total) {
            const res: AxiosResponse<AiAgentsResponse> = yield* withTenantRetry<AiAgentsResponse>(
                (token) =>
                    axios.get<AiAgentsResponse>(Config.VOICE_AI_ADAPTER_URL + '/voice-ai-agents', {
                        params: {limit, offset},
                        headers: {
                            accept: 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }),
            );
            const agents = res.data.agents ?? [];
            total = res.data.pagination?.total_records ?? offset + agents.length;
            match = agents.find((a) => a.sip_uri === sipUri) ?? null;
            offset += agents.length;
            if (agents.length === 0) break;
        }

        yield put(actions.findAiAgentBySipUri.success(match));
    } catch (e: any) {
        if (e instanceof AdapterUnauthorizedError) {
            yield put(actions.findAiAgentBySipUri.failure(undefined));
            return;
        }
        yield put(actions.findAiAgentBySipUri.failure(e?.response?.data));
    }
}

export const aiAgentsSaga = [
    takeLatest(actions.getAdapterHealth.request, getAdapterHealthSaga),
    takeLatest(actions.getVoiceAiConfig.request, getVoiceAiConfigSaga),
    takeLatest(actions.updateVoiceAiConfig.request, updateVoiceAiConfigSaga),
    takeLatest(actions.findAiAgentBySipUri.request, findAiAgentBySipUriSaga),
    takeEvery(actions.refreshTenant.request, refreshTenantSaga),
    takeLatest(actions.getAiAgentsList.request, getAiAgentsListSaga),
    takeEvery(actions.loadAiAgentsPage, loadAiAgentsPageSaga),
    takeLatest(actions.assignAgentPhone.request, assignAgentPhoneSaga),
];
